import { useCallback, useMemo } from "react";
import { useStopAgentExecution } from "@/queries/agent-queries";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useEnvironment, useCreateEnvironmentLogsToken } from "@/queries/environment-queries";
import { loadFromCache } from "@/utils/stream-cache";
import { getConversationCacheKey } from "@/utils/dashboard-conversation-stream";
import type JSZip from "jszip";

async function downloadEnvironmentSupportBundle(
    zip: JSZip,
    supportBundleUrl: string,
    logsToken: string,
): Promise<void> {
    try {
        const supportBundleResponse = await fetch(supportBundleUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${logsToken}`,
            },
        });

        if (!supportBundleResponse.ok) {
            const errorMessage = `Failed to download environment support bundle: ${supportBundleResponse.status} ${supportBundleResponse.statusText}\nTimestamp: ${new Date().toISOString()}`;
            zip.file("environment-support-bundle-error.txt", errorMessage);
            return;
        }

        const supportBundleBlob = await supportBundleResponse.blob();
        zip.file("environment-support-bundle.zip", supportBundleBlob);
    } catch (error) {
        const errorMessage = `Failed to download environment support bundle: ${String(error)}\nTimestamp: ${new Date().toISOString()}`;
        zip.file("environment-support-bundle-error.txt", errorMessage);
    }
}

async function downloadCachedConversationData(zip: JSZip, agentExecutionId: string): Promise<void> {
    try {
        const cacheKey = getConversationCacheKey(agentExecutionId);
        const cachedData = await loadFromCache(cacheKey);

        if (cachedData && cachedData.chunk.length > 0) {
            zip.file("conversation-stream.jsonl", cachedData.chunk);
        } else {
            const message = `No cached conversation data available for agent execution ${agentExecutionId}\nThis contains the conversation data as last seen by the user.\nTimestamp: ${new Date().toISOString()}`;
            zip.file("conversation-stream-unavailable.txt", message);
        }
    } catch (error) {
        const errorMessage = `Failed to load cached conversation data: ${String(error)}\nTimestamp: ${new Date().toISOString()}`;
        zip.file("conversation-stream-error.txt", errorMessage);
    }
}

export const useAgentExecutionActions = (agentExecution?: PlainAgentExecution) => {
    const stopAgentExecution = useStopAgentExecution();

    const lastEnvironmentId = useMemo(() => {
        return agentExecution?.status?.usedEnvironments?.[agentExecution.status.usedEnvironments.length - 1]
            ?.environmentId;
    }, [agentExecution?.status?.usedEnvironments]);

    const { data: environment } = useEnvironment(lastEnvironmentId);
    const { data: logsToken } = useCreateEnvironmentLogsToken(lastEnvironmentId);

    const copyAgentExecutionId = useCallback(async () => {
        if (!agentExecution?.id) {
            toast({ title: "No session ID available to copy" });
            return;
        }
        await navigator.clipboard.writeText(agentExecution.id);
        toast({ title: "Session ID copied to clipboard" });
    }, [agentExecution?.id]);

    const handleStopAgentExecution = useCallback(async () => {
        if (!agentExecution?.id) {
            toast({ title: "No session ID available to stop" });
            return;
        }
        try {
            await stopAgentExecution.mutateAsync({ agentExecutionId: agentExecution.id });
        } catch (error) {
            toast({
                title: "Failed to stop session",
                description: formatError(error),
            });
        }
    }, [agentExecution?.id, stopAgentExecution]);

    const downloadSupportBundle = useCallback(async () => {
        if (!agentExecution?.id) {
            return;
        }

        const supportBundleUrl = environment?.status?.environmentUrls?.supportBundle;

        if (!supportBundleUrl || !logsToken) {
            return;
        }

        try {
            const JSZip = (await import("jszip")).default;
            const zip = new JSZip();

            await downloadEnvironmentSupportBundle(zip, supportBundleUrl, logsToken);
            await downloadCachedConversationData(zip, agentExecution.id);

            const zipBlob = await zip.generateAsync({ type: "blob" });
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `support-bundle-${agentExecution.id}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({ title: "Support bundle download started" });
        } catch (error) {
            toast({
                title: "Failed to download support bundle",
                description: formatError(error),
            });
        }
    }, [environment?.status?.environmentUrls?.supportBundle, logsToken, agentExecution?.id]);

    const isSupportBundleAvailable = !!environment?.status?.environmentUrls?.supportBundle && !!logsToken;

    return {
        copyAgentExecutionId,
        handleStopAgentExecution,
        downloadSupportBundle,
        isStopPending: stopAgentExecution.isPending,
        isSupportBundleAvailable,
    };
};
